import { getDocumentValue } from '../site_documents/site_documents.helpers';
import { preferNonEmpty } from '../utils/prefer_non_empty';
import { isDocumentVoidable } from '../utils/signed_document_status.helpers';
/**
 * Placeholder domain the backend assigns to signers invited without a real address.
 * Classic rendered these as blank rather than exposing the synthetic address.
 */
const PLACEHOLDER_EMAIL_DOMAIN = '@econsent.invalid';
/** Statuses at which a document is finished and no longer accepts signing changes. */
const TERMINAL_STATUSES = ['cancelled', 'voided', 'complete'];
const isTerminal = (status) => TERMINAL_STATUSES.includes(String(status).toLowerCase());
/**
 * Whether this document follows the wet-signature (print-to-sign) workflow, and so gets
 * the printed action set rather than the digital one.
 *
 * The two flags answer different questions and both imply a wet signature:
 * `ec__pts` on the *document* records that this assignment is wet-signature, chosen per
 * assignment at creation (`buildInvitePayload` writes `signatureType === 'pts'`), while
 * `ec__pts_only` on the *template* records that the template permits nothing else, so
 * every document drawn from it is necessarily wet-signature.
 *
 * The divergence is designed, not incidental: the Cortex schema for the `ec__pts` property
 * on `ec__signed_document` (`econsent-enviroment/configuration/env/objects/ec__signed_document.json`)
 * states that it is copied from `ec__document_template.ec__pts_only` for legacy classic templates,
 * but set dynamically per assignment for Studio PDF templates — where `ec__pts_only` stays
 * false. So a Studio PDF document assigned as Print to Sign is wet-signature on the document
 * flag alone, and reading only the template would misclassify it.
 *
 * Reading the template alone — which is what this did — gives the digital action set to a
 * document created as printed from a mixed template, which is exactly what the site app's
 * Create consent modal produces, so its Type field said Printed while it offered digital
 * invites. Reading the document alone would be the mirror of that bug for a printed-only
 * template whose per-assignment flag was never written — classic did not write one. So it
 * is the disjunction, and the Type field is derived from this same predicate so the two
 * can no longer disagree.
 */
export const isPtsDocument = (document) => document?.ec__pts === true || document?.ec__document_template?.ec__pts_only === true;
/**
 * Whether the details page can offer Download or Print for this document.
 *
 * Digital copies are stamped from the Studio bundle. Printed (print-to-sign)
 * copies are the unsigned blank PDF — either the legacy `ec__pts_document` File
 * or the ICF PDF inside that same bundle — so a completed wet-signature
 * document stays printable (SSP-4679). `buildConsentCopyPdfBytes` refuses a
 * blank template once status is complete, which is why printed documents must
 * not reuse the digital stamp path.
 *
 * Print needs a fetchable `application/pdf` blob for Chrome's plugin. Legacy
 * File URLs are often cross-origin and cannot be fetched, so a File-URL-only
 * printed document can Download (navigate to the URL) but not Print. Status is
 * not a gate: SSP-3526 withholds complete / date-change / upload on voided
 * documents, not the printable copy, matching digital Download / Print.
 */
export const hasDocumentPdfCopySource = (document, mode = 'download') => {
    const hasBundle = !!document?.ec__published_studio_template?.ec__bundle?.url;
    if (!isPtsDocument(document)) {
        return hasBundle;
    }
    if (mode === 'print') {
        return hasBundle;
    }
    return hasBundle || !!document?.ec__document_template?.ec__pts_document?.url;
};
export const shouldDisplayFinalDocument = (document) => isPtsDocument(document) && !!document?.ec__final_document;
/**
 * Delayed signing is a control within digital signing, so it can never apply to a
 * wet-signature document, and it stops being meaningful once the document is terminal.
 */
export const shouldShowEnableSigningBadge = (document) => {
    if (isTerminal(document?.ec__status)) {
        return false;
    }
    if (isPtsDocument(document)) {
        return false;
    }
    return document?.ec__document_template?.ec__delayed_signing === true;
};
/** The badge shows on any delayed-signing document; the button only while still withheld. */
export const shouldShowEnableSigningButton = (document) => shouldShowEnableSigningBadge(document) && !document?.ec__can_sign_document;
/**
 * A wet signature is captured as a calendar date with no time of day, and
 * `complete_pts_document` stores it as a bare `YYYY-MM-DD` wallclock string in both
 * `ec__completion_time` and `ec__pts_signature_date_wallclock`. That string must never
 * reach an instant formatter: it parses as midnight UTC, so every viewer at a negative
 * offset is shown the previous day.
 *
 * `ec__pts_signature_date_wallclock` is the field of record. Rows written before it
 * existed only carry `ec__completion_time`, whose UTC calendar date is exactly what
 * classic displayed via `normalizeDateToUtc`, so taking the date part matches it.
 */
export const getPtsCompletionDate = (document) => (document?.ec__pts_signature_date_wallclock ?? document?.ec__completion_time ?? '').slice(0, 10);
/**
 * True for a bare `YYYY-MM-DD` with no time part. Whether a stored value is a wallclock
 * date is a property of the value itself, so testing it directly is safer than inferring
 * it from a PTS flag: a date written by the wet-signature path must still render
 * correctly if it is later read on a document the PTS predicate does not claim.
 */
export const isWallclockDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value ?? ''));
/**
 * Closed for changes because the document was withdrawn. Deliberately narrower than the
 * terminal set used for signing actions, which also excludes `complete`: a *completed*
 * wet-signature document is exactly the one whose date can still be corrected, whereas a
 * voided one can not.
 */
export const isDocumentVoided = (document) => !!document?.ec__status && !isDocumentVoidable(document.ec__status);
export const getVoidContext = (document) => {
    const raw = document?.ec__void_context;
    const entries = Array.isArray(raw) ? raw : (raw?.data ?? []);
    return entries[0];
};
export const isTemplateArchivedVoid = (document) => getVoidContext(document)?.ec__cause === 'template_archived';
/**
 * Cortex may project the participant number as a nested object or as a dotted key
 * when `ec__primary_participant` is not expanded.
 */
export const getPrimaryParticipantNumber = (document) => {
    if (!document) {
        return '';
    }
    const value = getDocumentValue(document, 'ec__primary_participant.c_number');
    return typeof value === 'string' ? value.trim() : '';
};
/**
 * Classic hid the signers row-action menu entirely for a wet-signature document — there
 * are no digital invites to manage — and once the document reached a terminal state.
 */
export const shouldShowSignerRowActions = (document) => !isPtsDocument(document) && !isTerminal(document?.ec__status);
/**
 * Sign-now visibility, ported from classic: a completed signer has nothing left to sign,
 * a row with no invite status is an optional signer, signing is sequential so the
 * preceding signer must have finished, and a site user does not sign for themselves from
 * this table.
 */
export const canSignNow = (row, rows, currentUserEmail) => {
    if (!row.status || row.status.toLowerCase() === 'complete') {
        return false;
    }
    if (row.email && currentUserEmail && row.email.toLowerCase() === currentUserEmail.toLowerCase()) {
        return false;
    }
    // Sorted by declared order rather than render order, and the predecessor is taken by
    // position so non-sequential order numbers still resolve correctly.
    const ordered = [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const previous = ordered[ordered.findIndex((candidate) => candidate.id === row.id) - 1];
    return !previous || previous.status?.toLowerCase() === 'complete';
};
/**
 * The participant's own address is owned by the participant record, so classic showed it
 * read-only in the invite form and only allowed editing a third party's address.
 */
const FIXED_EMAIL_SIGNER_TYPES = ['participant', 'non-signing participant'];
export const isEmailFixedForSignerType = (signerType) => FIXED_EMAIL_SIGNER_TYPES.includes(String(signerType).toLowerCase());
const normalizeEmail = (email) => (!email || email.endsWith(PLACEHOLDER_EMAIL_DOMAIN) ? '' : email);
/**
 * Cortex can hold several accepted-signer entries for one role — for example a
 * `connected` entry alongside the `complete` one — and only the completed entry carries
 * a time, so match on status rather than taking the first entry for the role.
 */
const getRoleCompletionTime = (document, role) => (document?.ec__accepted_signers ?? []).find((signer) => signer.ec__signer_role === role && signer.ec__status === 'complete')?.ec__completion_time ?? null;
/**
 * Completing a printed document does not reliably close its invites, so an invite left
 * open would read as still outstanding on a document that is already wet-signed. The
 * document status is the record for every template role, including roles printed create
 * never invites.
 */
const toWetSignedRow = (document) => (row) => ({
    ...row,
    status: 'complete',
    completionTime: row.completionTime || getPtsCompletionDate(document) || null,
});
/**
 * Builds one row per signer role declared on the template, filling in the invite where
 * one exists. Roles without an invite still get a row so the table reflects the signers
 * the document expects rather than only those already invited.
 *
 * Which template carries the roles depends on how the consent was authored: MOM writes
 * them onto the published Studio template and leaves the assignment shell's list empty,
 * while a classic template carries them on the shell. Reading only the shell dropped
 * every role of a Studio-published consent into the invites fallback below, which can
 * never show an unassigned role.
 */
const buildInviteSignerRows = (document, invites = []) => {
    const signerRoles = preferNonEmpty(document?.ec__published_studio_template?.ec__signer_roles, document?.ec__document_template?.ec__signer_roles);
    if (!signerRoles.length) {
        // No template roles to project against — fall back to the invites themselves so a
        // misconfigured template degrades to showing real data instead of an empty table.
        return invites.map((invite) => ({
            id: invite._id,
            order: invite.ec__order,
            email: normalizeEmail(invite.ec__email),
            signerRole: invite.ec__signer_role ?? '',
            status: invite.ec__status,
            // Signer type is declared on the template role, never on the invite, so this branch
            // has none to report. Left undefined rather than substituting the role, which would
            // feed the wrong value to the fixed-email rule.
            signerType: undefined,
            completionTime: getRoleCompletionTime(document, invite.ec__signer_role ?? ''),
            isUnassigned: false,
        }));
    }
    return signerRoles.map((signerRole, index) => {
        const invite = invites.find((candidate) => candidate.ec__signer_role === signerRole.ec__role);
        return {
            id: invite?._id ?? `unassigned-signer-${index}`,
            // Prefer the role's authored order; it is the ordering the template declares.
            order: signerRole.ec__order ?? invite?.ec__order,
            email: normalizeEmail(invite?.ec__email),
            signerRole: signerRole.ec__role,
            status: invite?.ec__status,
            signerType: signerRole.ec__signer_type,
            completionTime: getRoleCompletionTime(document, signerRole.ec__role),
            isUnassigned: !invite,
        };
    });
};
export const buildSignerRows = (document, invites = []) => {
    const rows = buildInviteSignerRows(document, invites);
    // Classic documents keep reading their invites as stored, matching the Classic Site App.
    // A Studio document is wet-signed only on its own `ec__pts`, the same test the server
    // applies before it accepts a printed completion and closes the invites.
    return document?.ec__published_studio_template && document.ec__pts === true && document.ec__status === 'complete'
        ? rows.map(toWetSignedRow(document))
        : rows;
};
